import { Component, Inject, OnDestroy, OnInit, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { AuthService } from '@auth0/auth0-angular';

import { Router } from '@angular/router';
import { UsersResource } from '../../../core/resources/users.resource';
import { environment } from '../../../../environments/environment';
import { UserService } from '../../../core/services/user.service';
import { catchError, filter, of, skip, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-main',
    templateUrl: './main.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    animations: [
        trigger('slideInOut', [
            transition(':enter', [
                style({ transform: 'translateX(-100%)' }),
                animate('300ms ease-in-out', style({ transform: 'translateX(0)' }))
            ]),
            transition(':leave', [animate('300ms ease-in-out', style({ transform: 'translateX(-100%)' }))])
        ]),
        trigger('fadeInOut', [
            transition(':enter', [style({ opacity: 0 }), animate('300ms ease-in-out', style({ opacity: 1 }))]),
            transition(':leave', [animate('300ms ease-in-out', style({ opacity: 0 }))])
        ]),
        trigger('backdropFade', [
            transition(':enter', [style({ opacity: 0 }), animate('300ms linear', style({ opacity: 1 }))]),
            transition(':leave', [animate('300ms linear', style({ opacity: 0 }))])
        ])
    ]
})
export class MainComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    public showUserMenu: boolean = false;
    public showSidebarMenu: boolean = false;

    public version = environment.version;
    public user: any;

    public navItems = [
        {
            label: 'Dashboard',
            route: '/dashboard',
            icon: '<svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>',
            exact: false
        },
        {
            label: 'Deployments',
            route: '/deployment',
            icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>',
            exact: false
        },
        {
            label: 'Services',
            route: '/service',
            icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>',
            exact: false
        },
        {
            label: 'Kafka',
            route: '/kafka',
            icon: '<svg fill="none" stroke="currentColor" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z" /></svg>',
            exact: false
        },
        {
            label: 'Tech Stack',
            route: '/tech-stack',
            icon: '<svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 0 0 2.25-2.25V6.75a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25Zm.75-12h9v9h-9v-9Z" /></svg>',
            exact: false
        },
        {
            label: 'Teams',
            route: '/team',
            icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>',
            exact: false
        },
        {
            label: 'Users',
            route: '/user',
            icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" /></svg>',
            exact: true
        }
    ];

    constructor(
        public readonly auth: AuthService,
        @Inject(DOCUMENT) public readonly document: Document,
        private readonly usersResource: UsersResource,
        private readonly userService: UserService,
        private readonly router: Router
    ) {}

    ngOnInit(): void {
        this.usersResource
            .getProfile()
            .pipe(
                catchError((error) => {
                    console.error('Failed to load user profile:', error);
                    // Return null to allow the component to render even without profile data
                    // This ensures the logout button remains accessible
                    return of(null);
                })
            )
            .subscribe((user) => {
                if (user) {
                    this.user = user;
                    this.userService.setUser(user);
                }
            });

        // Redirect to access-denied if auth state flips to false after the guard already let us through
        // (e.g. token expired while user was on a protected route)
        this.auth.isAuthenticated$
            .pipe(
                skip(1),
                filter((isAuthenticated) => !isAuthenticated),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.router.navigate(['/access-denied']);
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleMenu() {
        this.showUserMenu = !this.showUserMenu;
    }
    toggleMobileMenu() {
        this.showSidebarMenu = !this.showSidebarMenu;
    }
}
